import React, { useState, useEffect } from "react";
import {
  Table,
  Form,
  Row,
  Col,
  Card,
  Spinner,
  Button,
  Modal,
} from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Change } from "../../../../../store/async-actions/digitalmarketing/header";
import {
  useAddNewInvoiceMutation,
  useAddNewPaymentMutation,
  useGetProcedureTemplateListQuery,
} from "../../../../../Service/PatientList";
import { useLocation, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface Procedure {
  id: number;
  procedureTitle: string;
  price: number;
  cgst?: number;
  sgst?: number;
  notes?: string;
}

interface InvoiceItem {
  id: number;
  procedure: Procedure;
  units: number;
  discount: number;
  total: number;
}

interface Payment {
  type: "cash" | "card" | "online";
  amount: number;
  transactionNo?: string;
  cardNo?: string;
  paymentType?: string;
  remarks?: string;
}

const Invoices: React.FC = () => {
  const dispatch = useDispatch();

  const [invoiceCategory, setInvoiceCategory] = useState<string>("OPD");
  const [consultingDoctor, setConsultingDoctor] = useState<string>("Myself");
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [grandTotal, setGrandTotal] = useState<number>(0);
  const [selectedProcedure, setSelectedProcedure] = useState<string>("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState<Payment>({
    type: "cash",
    amount: 0,
    transactionNo: "",
    cardNo: "",
    paymentType: "",
    remarks: "",
  });
  const [invoiceSaved, setInvoiceSaved] = useState(false);

  const { data: procedureData, isLoading, isError, refetch } =
    useGetProcedureTemplateListQuery();
  const [createInvoice, { isLoading: savingInvoice }] =
    useAddNewInvoiceMutation();
  const [addPayment] = useAddNewPaymentMutation();

  const location = useLocation();
  const { id: patientId } = useParams();
  const { consultantId, appointmentId } = location.state as {
    consultantId: number;
    appointmentId: number;
  };

  useEffect(() => {
    dispatch(Change({ first: "Billing" } as any));
    refetch();
  }, [dispatch, refetch]);

  const procedures: Procedure[] = procedureData?.data || [];

  const handleSelectProcedure = (procedureId: string) => {
    if (!procedureId) return;
    const procedure = procedures.find((p) => p.id === Number(procedureId));
    if (!procedure) return;
    if (items.some((item) => item.procedure.id === procedure.id)) return;

    const gst = (Number(procedure.cgst) || 0) + (Number(procedure.sgst) || 0);
    const total =
      Number(procedure.price) + (Number(procedure.price) * gst) / 100;

    setItems((prev) => [
      ...prev,
      { id: Date.now(), procedure, units: 1, discount: 0, total: total || 0 },
    ]);
    setSelectedProcedure("");
  };

  const handleChange = (
    id: number,
    field: keyof InvoiceItem,
    value: number
  ) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          const gst =
            (Number(item.procedure.cgst) || 0) +
            (Number(item.procedure.sgst) || 0);
          const base = Number(item.procedure.price) * updated.units;
          const discountAmt = (base * updated.discount) / 100;
          const gstAmt = ((base - discountAmt) * gst) / 100;
          updated.total = base - discountAmt + gstAmt;
          return updated;
        }
        return item;
      })
    );
  };

  const handleRemoveRow = (id: number) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  useEffect(() => {
    const total = items.reduce((sum, i) => sum + Number(i.total || 0), 0);
    setGrandTotal(total);
  }, [items]);

  const numberToWords = (num: number): string => {
    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    const n = (num: number): string => {
      if (num < 20) return a[num];
      if (num < 100)
        return b[Math.floor(num / 10)] + (num % 10 ? " " + a[num % 10] : "");
      if (num < 1000)
        return (
          a[Math.floor(num / 100)] +
          " Hundred" +
          (num % 100 ? " and " + n(num % 100) : "")
        );
      return num.toString();
    };
    return `Rupees ${n(Math.floor(num))} Only`;
  };

  const handleSaveInvoice = async () => {
    if (!items.length) return toast.warning("Please add at least one procedure");

    const payload = {
      consultantId,
      patientId,
      appointmentId,
      category: invoiceCategory,
      treatments: items.map((item) => ({
        id: item.id || null,
        procedure: item.procedure.procedureTitle,
        procedureId: item.procedure.id,
        cost: item.procedure.price,
        cgst: item.procedure.cgst,
        sgst: item.procedure.sgst,
        notes: item.procedure.notes,
        units: item.units,
        discount: item.discount,
      })),
    };

    try {
      await createInvoice(payload).unwrap();
      toast.success("Invoice saved successfully!");
      setItems([]);
      setInvoiceSaved(true);
    } catch (error) {
      console.error(error);
      toast.error("Failed to save invoice");
    }
  };

  const handlePaymentSubmit = async () => {
    if (!paymentData.amount || paymentData.amount <= 0)
      return toast.warning("Please enter a valid amount");

    const payload: any = {
      appointment_id: appointmentId,
      patient_id: patientId,
      payment_mode: paymentData.type,
      amount: paymentData.amount,
      remarks: paymentData.remarks || "",
      card_no: paymentData.cardNo || "",
      transaction_no: paymentData.transactionNo || "",
    };

    if (paymentData.type === "online")
      payload.payment_type = paymentData.paymentType || "GPay";
    if (paymentData.type === "card") payload.payment_type = "Credit Card";

    try {
      await addPayment(payload).unwrap();
      toast.success("Payment submitted successfully!");
      setShowPaymentModal(false);
      setPaymentData({
        type: "cash",
        amount: 0,
        transactionNo: "",
        cardNo: "",
        paymentType: "",
        remarks: "",
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to submit payment");
    }
  };

  return (
    <div className="container mt-4">
      <ToastContainer position="top-right" autoClose={3000} />
      <Card className="shadow-sm border-0">
        <Card.Body>
          {/* Header */}
          <Row className="mb-3 align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Invoice Category</Form.Label>
                <Form.Select
                  value={invoiceCategory}
                  onChange={(e) => setInvoiceCategory(e.target.value)}
                >
                  <option value="OPD">OPD</option>
                  <option value="IPD">IPD</option>
                  <option value="Pharmacy">Pharmacy</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Consulting Doctor</Form.Label>
                <Form.Select
                  value={consultingDoctor}
                  onChange={(e) => setConsultingDoctor(e.target.value)}
                >
                  <option value="Myself">Myself</option>
                  <option value="Dr. Smith">Dr. Smith</option>
                  <option value="Dr. Anita">Dr. Anita</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Procedure Dropdown */}
          <Row className="mb-4 align-items-end">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Select Procedure</Form.Label>
                {isLoading ? (
                  <div className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" /> Loading...
                  </div>
                ) : isError ? (
                  <div className="text-danger small">
                    Failed to load procedures.
                  </div>
                ) : (
                  <Form.Select
                    value={selectedProcedure}
                    onChange={(e) => handleSelectProcedure(e.target.value)}
                  >
                    <option value="">Select</option>
                    {procedures.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.procedureTitle}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Procedure Table */}
          {items.length > 0 && (
            <>
              <Table bordered hover responsive>
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Procedure</th>
                    <th>Units</th>
                    <th>Price (₹)</th>
                    <th>Discount (%)</th>
                    <th>GST (%)</th>
                    <th>Total (₹)</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, idx) => {
                    const gst =
                      (Number(item.procedure.cgst) || 0) +
                      (Number(item.procedure.sgst) || 0);
                    return (
                      <tr key={item.id}>
                        <td>{idx + 1}</td>
                        <td>{item.procedure.procedureTitle}</td>
                        <td>
                          <Form.Control
                            type="number"
                            min={1}
                            value={item.units}
                            onChange={(e) =>
                              handleChange(
                                item.id,
                                "units",
                                Number(e.target.value)
                              )
                            }
                          />
                        </td>
                        <td>{Number(item.procedure.price || 0).toFixed(2)}</td>
                        <td>
                          <Form.Control
                            type="number"
                            min={0}
                            value={item.discount}
                            onChange={(e) =>
                              handleChange(
                                item.id,
                                "discount",
                                Number(e.target.value)
                              )
                            }
                          />
                        </td>
                        <td>{gst}</td>
                        <td>{Number(item.total || 0).toFixed(2)}</td>
                        <td>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleRemoveRow(item.id)}
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {/* Grand Total */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  <strong>Total in Words:</strong> {numberToWords(grandTotal)}
                </div>
                <div className="text-end">
                  <strong style={{ color: "#6a0dad", fontSize: "1.1rem" }}>
                    Total: ₹{grandTotal.toFixed(2)}
                  </strong>
                </div>
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="d-flex justify-content-end mt-4 gap-3">
            {invoiceSaved && (
              <Button
                variant="primary"
                style={{
                  backgroundColor: "#6A0DAD",
                  border: "none",
                  borderRadius: "20px",
                  padding: "8px 20px",
                }}
                onClick={() => setShowPaymentModal(true)}
              >
                <i className="bi bi-plus-lg"></i> Add Payment
              </Button>
            )}
            <Button
              variant="success"
              style={{
                backgroundColor: "#28a745",
                border: "none",
                borderRadius: "20px",
                padding: "8px 20px",
              }}
              onClick={handleSaveInvoice}
              disabled={savingInvoice}
            >
              {savingInvoice ? (
                "Saving..."
              ) : (
                <>
                  <i className="bi bi-save"></i> Save Invoice
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Payment Modal */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Payment Mode</Form.Label>
              <Form.Select
                value={paymentData.type}
                onChange={(e) => {
                  const type = e.target.value as Payment["type"];
                  setPaymentData({
                    type,
                    amount: 0,
                    transactionNo: "",
                    cardNo: "",
                    paymentType: "",
                    remarks: "",
                  });
                }}
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="online">Online</option>
              </Form.Select>
            </Form.Group>

            {paymentData.type === "card" && (
              <Form.Group className="mb-3">
                <Form.Label>Card No</Form.Label>
                <Form.Control
                  type="text"
                  value={paymentData.cardNo}
                  onChange={(e) =>
                    setPaymentData({ ...paymentData, cardNo: e.target.value })
                  }
                />
              </Form.Group>
            )}

            {paymentData.type === "online" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Type</Form.Label>
                  <Form.Select
                    value={paymentData.paymentType || ""}
                    onChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        paymentType: e.target.value,
                      })
                    }
                  >
                    <option value="">Select</option>
                    <option value="GPay">GPay</option>
                    <option value="PhonePe">PhonePe</option>
                    <option value="Paytm">Paytm</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Transaction No</Form.Label>
                  <Form.Control
                    type="text"
                    value={paymentData.transactionNo}
                    onChange={(e) =>
                      setPaymentData({
                        ...paymentData,
                        transactionNo: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                value={paymentData.amount === 0 ? "" : paymentData.amount}
                placeholder="Enter amount"
                onChange={(e) =>
                  setPaymentData({
                    ...paymentData,
                    amount: Number(e.target.value),
                  })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                type="text"
                value={paymentData.remarks}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, remarks: e.target.value })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPaymentModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handlePaymentSubmit}>
            Submit Payment
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Invoices;
